import { useMemo } from "react";
import {
  splitTextWithAllUrls,
  MENTION_REGEX,
  type MentionMap,
} from "@tinyclub/shared/utils";
import { useHandleLinkPress } from "@/utils/linkUtils";
import { useProfileSidePanel } from "@/components/profile/ProfileSidePanelProvider";
import { cn } from "./cn";

// Re-export MentionMap for convenience
export type { MentionMap } from "@tinyclub/shared/utils";

interface RichBodyTextProps {
  /** The text content to render */
  text: string;
  /** Additional CSS classes */
  className?: string;
  /** Map of usernames to user IDs for clickable mentions */
  mentionMap?: MentionMap;
  /** Maximum characters for truncated URLs (default: 24) */
  maxUrlLength?: number;
  /** Callback when non-interactive text is pressed */
  onClick?: () => void;
}

/** Internal type for parsed segments */
type RichTextSegment = {
  type: "text" | "url" | "email" | "mention";
  text: string;
  username?: string; // For mentions - the original username from text
  userId?: string; // For mentions with valid user ID
  displayName?: string; // For mentions - the name to display (First Last)
  linkUrl?: string; // For Markdown links - the actual URL (text is display text)
};

/**
 * Parse text into segments of plain text, URLs, emails, and mentions
 * URLs/emails are parsed first, then mentions are found within text segments
 */
function parseRichText(text: string, mentionMap?: MentionMap): RichTextSegment[] {
  if (!text) return [];

  // First pass: split by URLs and emails
  const urlParts = splitTextWithAllUrls(text);
  const segments: RichTextSegment[] = [];

  for (const part of urlParts) {
    if (part.isUrl) {
      // For Markdown links, text is display text and linkUrl is the actual URL
      segments.push({
        type: "url",
        text: part.text,
        linkUrl: part.isMarkdownLink ? part.linkUrl : undefined,
      });
    } else if (part.isEmail) {
      segments.push({ type: "email", text: part.text });
    } else {
      // For plain text segments, look for mentions
      const mentionSegments = parseMentionsInTextSegment(part.text, mentionMap);
      segments.push(...mentionSegments);
    }
  }

  return segments;
}

/**
 * Parse mentions within a text segment (that doesn't contain URLs/emails)
 */
function parseMentionsInTextSegment(
  text: string,
  mentionMap?: MentionMap
): RichTextSegment[] {
  if (!text) return [];

  const segments: RichTextSegment[] = [];
  let lastIndex = 0;

  // Reset regex state
  const regex = new RegExp(MENTION_REGEX.source, "g");
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    // Add text before this mention
    if (match.index > lastIndex) {
      segments.push({
        type: "text",
        text: text.slice(lastIndex, match.index),
      });
    }

    const username = match[1];
    const userData = mentionMap?.[username.toLowerCase()];

    // Add the mention - display name from map, or fall back to username
    segments.push({
      type: "mention",
      text: userData?.displayName || username, // Display "First Last" if available
      username,
      userId: userData?.userId,
      displayName: userData?.displayName,
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last mention
  if (lastIndex < text.length) {
    segments.push({
      type: "text",
      text: text.slice(lastIndex),
    });
  }

  // If no mentions found, return the original text as a single segment
  if (segments.length === 0) {
    return [{ type: "text", text }];
  }

  return segments;
}

/**
 * RichBodyText renders text with styled mentions, URLs, and emails.
 *
 * - Mentions are rendered in accent-secondary color (without @) and are clickable if a valid user ID is found
 * - URLs are rendered in blue and open in browser when clicked
 * - Emails are rendered in blue and open mailto: when clicked
 */
export function RichBodyText({
  text,
  className,
  mentionMap,
  maxUrlLength = 24,
  onClick,
}: RichBodyTextProps) {
  const handleLinkPress = useHandleLinkPress();
  const { openProfile } = useProfileSidePanel();

  // Parse text into segments
  const segments = useMemo(
    () => parseRichText(text, mentionMap),
    [text, mentionMap]
  );

  // Handle mention click - open profile in side panel
  const handleMentionClick = (e: React.MouseEvent, userId: string) => {
    e.stopPropagation();
    e.preventDefault();
    openProfile(userId);
  };

  return (
    <p
      className={cn("text-body-2 text-text-primary whitespace-pre-wrap", className)}
      onClick={onClick}
    >
      {segments.map((segment, index) => {
        switch (segment.type) {
          case "url": {
            // For Markdown links, linkUrl is the actual URL and text is display text
            const actualUrl = segment.linkUrl || segment.text;
            const displayText = segment.linkUrl
              ? segment.text // Markdown link: show full display text
              : segment.text.length > maxUrlLength
                ? `${segment.text.substring(0, maxUrlLength)}...`
                : segment.text;

            return (
              <span
                key={index}
                className="text-blue cursor-pointer hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  handleLinkPress(actualUrl);
                }}
              >
                {displayText}
              </span>
            );
          }

          case "email":
            return (
              <span
                key={index}
                className="text-blue cursor-pointer hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  handleLinkPress(segment.text);
                }}
              >
                {segment.text}
              </span>
            );

          case "mention":
            if (segment.userId) {
              // Use span with onClick to avoid nested <a> tags when inside a Link
              return (
                <span
                  key={index}
                  className="text-accent-secondary font-medium hover:underline cursor-pointer"
                  onClick={(e) => handleMentionClick(e, segment.userId!)}
                >
                  {segment.text}
                </span>
              );
            }
            // Mention without valid user ID - just styled, not clickable
            return (
              <span
                key={index}
                className="text-accent-secondary font-medium"
              >
                {segment.text}
              </span>
            );

          case "text":
          default:
            return <span key={index}>{segment.text}</span>;
        }
      })}
    </p>
  );
}

export default RichBodyText;
